import React, { memo, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import styles from 'assets/css/ProgressWheel.module.css';
import CardLink from 'Components/Card/CardLink';
import { IconAngleRight, IconTargetArrows, IconStarCircleWreath } from '@ace/react-icons';
import { allActions } from '@ace/react-utilities';
import Canvas from 'Components/Canvas';

function CanvasProgressWheel({canvasCircleProps, modules, ...props}) {
    const mappedModules = useMemo(() => {
        const modulesCount = modules.length;
        const startingDegree = 270;
        const sizeOfEachPortion = 360 / modulesCount;

        return modules.map((sgMod, index) => ({
            position: index + 1,
            startDegree: (startingDegree + (index * sizeOfEachPortion)) % 360,
            endDegree: (startingDegree + ((index + 1) * sizeOfEachPortion)) % 360,
            originStartDegree: ((startingDegree + (index * sizeOfEachPortion)) + sizeOfEachPortion/2) % 360,
            percentComplete: sgMod.PercentageComplete,
            title: sgMod.ModuleTitle,
            sizeOfEachPortion }));
    }, [modules]);

    const draw = useCallback((context) => {
        const radius = canvasCircleProps.radius;
        const circleOrigin = { x: radius, y: radius };
        const circleBackgroundColor = canvasCircleProps.backgroundColor;
        const circleProgressColor = canvasCircleProps.progressColor;
        const circleSeparatorColor = canvasCircleProps.separatorColor;

        const modulesCount = mappedModules.length;

        const drawPartialCircle = (color, startAngleDeg = 0, percentComplete = 0) => {
            const startAngle = startAngleDeg * (Math.PI / 180);
            const endAngle = startAngle + Math.PI * ((2 / modulesCount) * (percentComplete / 100));
            context.fillStyle = color;
            context.beginPath();
            context.moveTo(circleOrigin.x, circleOrigin.y);
            context.arc(circleOrigin.x, circleOrigin.y, radius, startAngle, endAngle, false);
            context.fill();
        }

        const drawNumericalLabel = (origin, number) => {
            let {x, y} = origin;
            let w = 26;
            context.beginPath();
            context.fillStyle = "#02263c";
            context.arc(x, y, w/2, 0, 2 * Math.PI, false);
            context.fill();

            context.beginPath();
            context.fillStyle = "#d2efea";
            context.arc(x, y, (w-2)/2, 0, 2 * Math.PI, false);
            context.fill();

            context.font = "900 12px Muli";
            context.fillStyle = "#02263c";
            context.fillText(number, origin.x - 3, origin.y + 4);
        }

        const getOrigin = (startAngleDeg) => {
            const x = (radius*3/4) * Math.cos(startAngleDeg * (Math.PI / 180)) + radius;
            const y = (radius*3/4) * Math.sin(startAngleDeg * (Math.PI / 180)) + radius;

            return { x, y };
        };

        mappedModules.forEach(ele => {
            drawPartialCircle(circleBackgroundColor, ele.startDegree, 100);
            drawPartialCircle(circleProgressColor, ele.startDegree, ele.percentComplete);

            const origin = getOrigin(ele.originStartDegree);

            drawNumericalLabel(origin, ele.position);
        });

        mappedModules.forEach(ele => {
            drawPartialCircle(circleSeparatorColor, ele.startDegree-3, 6);
        });

    }, [mappedModules, canvasCircleProps]);

    return (
        <Canvas {...props} draw={draw} />
    );
}

function OverviewProgressComponent({modules, percentageComplete, ...props}) {
    return (
        <div className={styles["progress-wheel-outer"]}>
            <div className={styles["progress-wheel-inner"]}>
                <div className={styles["progress-wheel-center"]}>
                    <IconStarCircleWreath />
                </div>
                <CanvasProgressWheel {...props} modules={modules} />
            </div>
        </div>
    );
}

export const defaultCanvasProps = {
    radius: 100,
    backgroundColor: 'rgba(74,191,173,.25)',
    progressColor: 'rgba(74,191,173,1)',
    separatorColor: 'rgba(255,255,255,1)'
};

export default OverviewProgressComponent;
